/**
 * API calls for the RLHF simulation-rating workbench.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'` —
 * there is no bearer token anywhere in this codebase.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export interface RatingAssignment {
  id: number;
  /**
   * The item ref — a bare-integer simulation id ("42") OR a chat-session ref ("chat_9"). String,
   * not number, because a rating item is polymorphic (simulation or hcv2 chat session).
   */
  simulation_id: string;
  status: string;
  rating: number | null;
  remarks: string | null;
  assigned_at: string | null;
  submitted_at: string | null;
  updated_at: string | null;
}

export interface AssignmentSummary {
  id: number;
  /** Item ref: bare-integer sim id or `chat_<id>`. See RatingAssignment.simulation_id. */
  simulation_id: string;
  simulation_name: string | null;
  use_case: string | null;
  status: string;
  rating: number | null;
  /** Carried so revisiting a rated simulation restores the reasoning, not just the score. */
  remarks: string | null;
  /** True once terminally handled — rated OR skipped-with-reason. Drives "first unhandled" landing. */
  is_rated: boolean;
  /** Distinguishes a skip from a real score, so the queue can label it and show no stars. */
  is_skipped: boolean;
}

export interface RatingProgress {
  rated: number;
  /** Terminal skips — counted apart from `rated` (a skip is not a score). */
  skipped: number;
  pending: number;
  available: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (response.status === 403) {
    throw new Error('You do not have rater access. Ask an admin to enable it for your account.');
  }
  if (!response.ok) {
    // Surface the backend's own message where it has one — the rating endpoint explains
    // exactly why a submission was rejected (missing score, remarks too short).
    let detail = '';
    try {
      detail = (await response.json())?.detail ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  // 204 = the pool is exhausted and the rater has nothing pending.
  if (response.status === 204) return null as T;
  return response.json();
}

/**
 * Advance to the next simulation needing judgment. Null when nothing is left.
 *
 * `excludeSimulationId` is passed by Skip: it just released that simulation, and the pool is
 * ordered newest-first, so without excluding it the claim would hand the same one straight back.
 */
export const fetchNextAssignment = (excludeSimulationId?: string) => {
  const qs = excludeSimulationId != null
    ? `?exclude_simulation_id=${encodeURIComponent(excludeSimulationId)}` : '';
  return request<RatingAssignment | null>(`/rlhf/assignments/next${qs}`, { method: 'POST' });
};

/** The rater's whole queue, oldest first — drives Prev/Next and revisiting. */
export const fetchAssignments = () =>
  request<{ assignments: AssignmentSummary[] }>('/rlhf/assignments').then((r) => r.assignments);

export const fetchProgress = () => request<RatingProgress>('/rlhf/progress');

/**
 * Save or revise a rating. Idempotent — this is what makes both autosave and
 * after-the-fact editing work. `draft` persists partial work without marking the
 * assignment rated.
 */
export const saveRating = (
  assignmentId: number,
  body: { rating: number | null; remarks: string; draft: boolean },
) =>
  request<RatingAssignment>(`/rlhf/assignments/${assignmentId}/rating`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const releaseAssignment = (assignmentId: number) =>
  request<{ released: boolean }>(`/rlhf/assignments/${assignmentId}/release`, { method: 'POST' });

/**
 * Terminally skip an item with a required reason. Unlike release, the item is closed out and
 * never re-served — this is how a rater declines an item they can't score without it looping back.
 */
export const skipAssignment = (assignmentId: number, reason: string) =>
  request<RatingAssignment>(`/rlhf/assignments/${assignmentId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
